const ENDPOINT = "/engagement_periods";
const STORAGE_PREFIX = "sq_engagement:";
const IDLE_MS = 2 * 60 * 1000;
const TIMEOUT_MS = 30 * 60 * 1000;
const SNAPSHOT_MS = 5000;
const SHORT_SEARCH_MS = 3000;
const NO_LOCKS_RECOVERY_MS = 35 * 60 * 1000;
const MAX_ROWS_PER_SEND = 20;
const INPUT_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

let state = null;
let pendingTabKey = null;

const uuid = () => crypto.randomUUID();

const storage = {
  get(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      return;
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      return;
    }
  },
  keys() {
    try {
      return Object.keys(localStorage).filter((key) => key.startsWith(STORAGE_PREFIX));
    } catch {
      return [];
    }
  },
};

const newPeriod = (kind, parentId, startedAt, properties = {}) => ({
  id: uuid(),
  kind,
  parent_id: parentId,
  started_at: startedAt,
  ended_at: null,
  end_reason: null,
  active_ms: 0,
  away_ms: 0,
  idle_ms: 0,
  away_count: 0,
  properties,
});

const toRow = (period) => ({
  id: period.id,
  kind: period.kind,
  parent_id: period.parent_id,
  started_at: new Date(period.started_at).toISOString(),
  ended_at: period.ended_at ? new Date(period.ended_at).toISOString() : null,
  end_reason: period.end_reason,
  active_seconds: Math.round(period.active_ms / 1000),
  away_seconds: Math.round(period.away_ms / 1000),
  properties: {
    ...period.properties,
    away_count: period.away_count,
    idle_seconds: Math.round(period.idle_ms / 1000),
  },
});

const post = (rows) =>
  fetch(ENDPOINT, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ periods: rows }),
  }).then((response) => {
    if (!response.ok && response.status !== 401) throw new Error(`engagement ${response.status}`);
    return response;
  });

const openPeriods = () => [state.session, state.tab, state.search].filter(Boolean);

const computeMode = (now) => {
  const focused = document.hasFocus() || document.activeElement?.tagName === "IFRAME";
  if (document.visibilityState !== "visible" || !focused) return "away";
  if (now - state.lastInputAt >= IDLE_MS) return "idle";
  return "active";
};

const accrue = (now) => {
  const delta = Math.max(0, now - state.modeSince);
  openPeriods().forEach((period) => {
    if (state.mode === "active") period.active_ms += delta;
    else period.away_ms += delta;
    if (state.mode === "idle") period.idle_ms += delta;
  });
  if (state.mode !== "active" && state.stretch) {
    state.stretch.away_ms += delta;
    if (state.mode === "idle") state.stretch.idle_ms += delta;
  }
  state.modeSince = now;
};

const snapshotKey = () => `${STORAGE_PREFIX}${state.session.id}`;

const writeSnapshot = (clean = false) => {
  if (!state) return;
  storage.set(snapshotKey(), {
    recruiterId: state.recruiterId,
    sessionId: state.session.id,
    openPeriods: clean ? [] : openPeriods(),
    unsent: Array.from(state.unsent.values()),
    lastSnapshotAt: Date.now(),
    lastState: state.mode,
    clean,
  });
};

const queue = (periods) => {
  periods.forEach((period) => state.unsent.set(period.id, toRow(period)));
};

const flush = () => {
  if (!state || state.unsent.size === 0) return;
  const rows = Array.from(state.unsent.values()).slice(-MAX_ROWS_PER_SEND);
  const snapshot = new Map(rows.map((row) => [row.id, row]));
  const key = snapshotKey();
  const current = state;
  post(rows)
    .then(() => {
      snapshot.forEach((row, id) => {
        if (current.unsent.get(id) === row) current.unsent.delete(id);
      });
      if (current.closed) {
        if (current.unsent.size === 0) storage.remove(key);
      } else if (state === current) {
        writeSnapshot();
      }
    })
    .catch(() => undefined);
};

const send = (periods) => {
  queue(periods);
  writeSnapshot(state.closed);
  flush();
};

const endPeriod = (period, reason, at) => {
  if (!period || period.ended_at) return period;
  period.ended_at = at;
  period.end_reason = reason;
  return period;
};

const endSearch = (reason, at) => {
  const search = endPeriod(state.search, reason, at);
  state.search = null;
  return search;
};

const endTab = (reason, searchReason, at) => {
  const search = endSearch(searchReason, at);
  const tab = endPeriod(state.tab, reason, at);
  state.tab = null;
  return [search, tab].filter(Boolean);
};

const openSession = (now, tabKey, searchProperties) => {
  state.session = newPeriod("session", null, now);
  state.tab = tabKey ? newPeriod(tabKey, state.session.id, now) : null;
  state.search = searchProperties ? newPeriod("search", state.session.id, now, searchProperties) : null;
  state.mode = computeMode(now);
  state.modeSince = now;
  state.stretch = state.mode === "active" ? null : { since: now, away_ms: 0, idle_ms: 0 };
  acquireLock(state.session.id);
};

const endSession = (reason, at) => {
  const ended = [...endTab(reason, reason, at), endPeriod(state.session, reason, at)];
  releaseLock();
  return ended;
};

const restartAfterTimeout = (now) => {
  const { since, away_ms: awayMs, idle_ms: idleMs } = state.stretch;
  const reason = idleMs * 2 > awayMs ? "idle_timeout" : "away_timeout";
  openPeriods().forEach((period) => {
    period.away_ms = Math.max(0, period.away_ms - awayMs);
    period.idle_ms = Math.max(0, period.idle_ms - idleMs);
  });
  const tabKey = state.tab?.kind;
  const searchProperties = state.search?.properties;
  const ended = endSession(reason, since);
  storage.remove(snapshotKey());
  queue(ended);
  openSession(now, tabKey, searchProperties);
  send(openPeriods());
};

const transition = () => {
  if (!state || state.closed) return;
  const now = Date.now();
  accrue(now);
  const next = computeMode(now);
  if (next === state.mode) return;
  const previous = state.mode;

  if (next === "active") {
    const stretch = state.stretch;
    state.stretch = null;
    if (stretch && now - stretch.since >= TIMEOUT_MS) {
      state.stretch = stretch;
      restartAfterTimeout(now);
      return;
    }
    state.mode = next;
    writeSnapshot();
    return;
  }

  if (previous === "active") state.stretch = { since: now, away_ms: 0, idle_ms: 0 };
  if (next === "away") openPeriods().forEach((period) => (period.away_count += 1));
  state.mode = next;
  if (next === "idle" || document.visibilityState !== "visible") send(openPeriods());
  else writeSnapshot();
};

const onInput = () => {
  if (!state) return;
  state.lastInputAt = Date.now();
  if (state.mode !== "active") transition();
};

const scheduleIdleCheck = () => {
  if (!state) return;
  clearTimeout(state.idleTimer);
  const wait = Math.max(1000, IDLE_MS - (Date.now() - state.lastInputAt));
  state.idleTimer = setTimeout(() => {
    if (!state) return;
    if (state.mode === "active" && Date.now() - state.lastInputAt >= IDLE_MS) transition();
    scheduleIdleCheck();
  }, wait);
};

const acquireLock = (sessionId) => {
  if (!navigator.locks) return;
  const holder = state;
  navigator.locks
    .request(`${STORAGE_PREFIX}${sessionId}`, () =>
      new Promise((resolve) => {
        if (holder.session?.id === sessionId && !holder.stopped) holder.releaseLock = resolve;
        else resolve();
      })
    )
    .catch(() => undefined);
};

const releaseLock = () => {
  if (state?.releaseLock) state.releaseLock();
  if (state) state.releaseLock = null;
};

const recoverSnapshot = (key, snapshot, recruiterId) => {
  if (!snapshot || snapshot.recruiterId !== recruiterId) {
    storage.remove(key);
    return;
  }
  const reason = snapshot.lastState === "away" ? "killed_in_background" : "browser_crashed";
  const ended = (snapshot.openPeriods || []).map((period) =>
    toRow({ ...period, ended_at: period.ended_at || snapshot.lastSnapshotAt, end_reason: period.end_reason || reason })
  );
  const byId = new Map((snapshot.unsent || []).map((row) => [row.id, row]));
  ended.forEach((row) => byId.set(row.id, row));
  const rows = Array.from(byId.values());
  if (rows.length === 0) {
    storage.remove(key);
    return;
  }
  post(rows.slice(-MAX_ROWS_PER_SEND))
    .then(() => storage.remove(key))
    .catch(() => undefined);
};

const recoverOrphans = async (recruiterId) => {
  const ownKey = state ? snapshotKey() : null;
  const keys = storage.keys().filter((key) => key !== ownKey);
  if (keys.length === 0) return;
  let held = null;
  if (navigator.locks?.query) {
    try {
      const result = await navigator.locks.query();
      held = new Set((result.held || []).map((lock) => lock.name));
    } catch {
      held = null;
    }
  }
  keys.forEach((key) => {
    const snapshot = storage.get(key);
    if (!snapshot) return storage.remove(key);
    if (snapshot.clean) return recoverSnapshot(key, { ...snapshot, openPeriods: [] }, recruiterId);
    const dead = held ? !held.has(key) : Date.now() - snapshot.lastSnapshotAt > NO_LOCKS_RECOVERY_MS;
    if (dead) recoverSnapshot(key, snapshot, recruiterId);
  });
};

const onVisibility = () => transition();
const onFocusChange = () => transition();

const onPageHide = () => {
  if (!state || state.closed) return;
  const now = Date.now();
  accrue(now);
  state.closed = true;
  send(endSession("closed", now));
};

const onPageShow = (event) => {
  if (!event.persisted || !state?.closed) return;
  const { recruiterId, lastTabKey } = state;
  stop();
  start({ recruiterId, tabKey: lastTabKey });
};

const listeners = [
  [document, "visibilitychange", onVisibility],
  [window, "focus", onFocusChange],
  [window, "blur", onFocusChange],
  [window, "pagehide", onPageHide],
  [window, "pageshow", onPageShow],
  ...INPUT_EVENTS.map((name) => [window, name, onInput]),
];

const start = ({ recruiterId, tabKey = pendingTabKey }) => {
  if (state || !recruiterId || typeof window === "undefined" || !window.crypto?.randomUUID) return;
  const now = Date.now();
  state = {
    recruiterId,
    unsent: new Map(),
    lastInputAt: now,
    lastTabKey: tabKey,
    closed: false,
    releaseLock: null,
  };
  openSession(now, tabKey, null);
  listeners.forEach(([target, name, handler]) => target.addEventListener(name, handler, { passive: true }));
  state.snapshotTimer = setInterval(() => {
    if (!state || state.closed || state.mode !== "active") return;
    accrue(Date.now());
    writeSnapshot();
  }, SNAPSHOT_MS);
  scheduleIdleCheck();
  send(openPeriods());
  recoverOrphans(recruiterId);
};

const stop = () => {
  if (!state) return;
  clearInterval(state.snapshotTimer);
  clearTimeout(state.idleTimer);
  listeners.forEach(([target, name, handler]) => target.removeEventListener(name, handler));
  releaseLock();
  state.stopped = true;
  state = null;
};

const switchTab = (tabKey) => {
  pendingTabKey = tabKey || null;
  if (!state || state.closed) return;
  state.lastTabKey = tabKey;
  if ((state.tab?.kind || null) === (tabKey || null)) return;
  const now = Date.now();
  accrue(now);
  const ended = endTab("switched_tab", "left_tab", now);
  if (tabKey) state.tab = newPeriod(tabKey, state.session.id, now);
  send([...ended, state.tab].filter(Boolean));
};

const startSearch = (properties = {}) => {
  if (!state || state.closed) return null;
  const now = Date.now();
  accrue(now);
  const current = state.search;
  if (current && now - current.started_at < SHORT_SEARCH_MS) {
    current.properties = { ...current.properties, ...properties };
    send([current]);
    return current.id;
  }
  const ended = endSearch("refined", now);
  state.search = newPeriod("search", state.session.id, now, properties);
  send([ended, state.search].filter(Boolean));
  return state.search.id;
};

const clearSearch = () => {
  if (!state || state.closed || !state.search) return;
  const now = Date.now();
  accrue(now);
  send([endSearch("cleared", now)]);
};

const endAll = (reason) => {
  if (!state || state.closed) return;
  const now = Date.now();
  accrue(now);
  state.closed = true;
  send(endSession(reason, now));
};

const currentPeriodId = () => {
  if (!state || state.closed) return null;
  return state.search?.id || state.tab?.id || null;
};

export default { start, stop, switchTab, startSearch, clearSearch, endAll, currentPeriodId };
